<template>
	<div class="xs-12">
		<!-- display current logic -->
		<div class="xs-12 b1 bc7 r2 p2">
			<!-- Choose achievement logic type (simple, and, or) -->
			<div class="flex p2 xs-between">
				<j-base-radios
					size="xsmall"
					:options="achievementLogicTypes"
					:value="selectedAchievementLogicType"
					@input="onChangeAchievementType"
				/>
				<div v-if="showRemoveBtn" class="cu2" @click="$emit('remove')">
					<crm-icon name="delete" color="cError" />
				</div>
			</div>

			<!-- And -->
			<div v-if="selectedAchievementLogicType === 'and'">
				<j-button class="mb2" size="xsmall" @click="addLogicToAnd"
					>+{{ $t("add_achievement_logic") }}</j-button
				>
				<div
					class="flex mb2"
					v-for="(andAchievementLogic, i) in andAchievementLogicList"
					:key="i"
				>
					<achievement-logic
						:showRemoveBtn="true"
						:value="andAchievementLogic"
						@input="updateLogicOfAnd(i, $event)"
						@remove="removeLogicFromAnd(i)"
					/>
				</div>
			</div>

			<!-- Or -->
			<div v-if="selectedAchievementLogicType === 'or'">
				<j-button class="mb2" size="xsmall" @click="addLogicToOr"
					>+{{ $t("add_achievement_logic") }}</j-button
				>
				<div
					class="flex mb2"
					v-for="(orAchievementLogic, i) in orAchievementLogicList"
					:key="i"
				>
					<achievement-logic
						:showRemoveBtn="true"
						:value="orAchievementLogic"
						@input="updateLogicOfOr(i, $event)"
						@remove="removeLogicFromOr(i)"
					/>
				</div>
			</div>

			<!-- Simple -->
			<div class="flex" v-if="selectedAchievementLogicType === 'simple'">
				<!-- Query -->
				<div class="xs-6 pr2">
					<div class="xs-12 fs3 c6 fw7 pb2">{{ $t("graphql_query") }}</div>
					<div class="xs-12 b1 bc7 r2 p4" v-if="!isLoading">
						<div v-if="computedQuery">
							<core-monaco-editor :readOnly="true" :value="computedQuery" />
							<j-button
								class="mt2"
								size="xsmall"
								@click="$modal.show(`inputQueryModal-${modalKey}`)"
							>
								{{ $t("edit_query") }}
							</j-button>
						</div>
						<div v-else class="flex xs-middle xs-center">
							<j-button
								type="tertiary"
								size="small"
								@click="$modal.show(`inputQueryModal-${modalKey}`)"
							>
								{{ $t("add_query") }}
							</j-button>
						</div>
					</div>
				</div>

				<!-- Liquid condition -->
				<div class="pl2 xs-6">
					<div class="xs-12 fs3 c6 fw7 pb2">{{ $t("success_condition") }}</div>
					<div class="xs-12 b1 bc7 r2 p4">
						<div v-if="computedLiquidCondition">
							<core-monaco-editor
								:readOnly="true"
								:value="computedLiquidCondition"
							/>
							<j-button
								class="mt2"
								size="xsmall"
								@click="$modal.show(`inputLiquidConditionModal-${modalKey}`)"
							>
								{{ $t("edit_success_condition") }}
							</j-button>
						</div>
						<div v-else class="flex xs-middle xs-center">
							<j-button
								type="tertiary"
								size="small"
								@click="$modal.show(`inputLiquidConditionModal-${modalKey}`)"
							>
								{{ $t("add_success_condition") }}
							</j-button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Input query modal -->
		<core-modal
			:name="`inputQueryModal-${modalKey}`"
			:headline="$t('graphql_query')"
		>
			<div class="xs-12 p4">
				<div class="xs-12 fs3 c6 fw7 pb2">{{ $t("graphql_query") }}</div>
				<!-- select template -->
				<j-base-select
					size="small"
					:options="graphQlQueries"
					class="xs-12"
					v-model="selectedQueryTemplateId"
				/>
				<!-- query args -->
				<div
					v-if="computedQueryTemplate.args && computedQueryTemplate.args.length"
					class="bt1 bc7 mt6 pt2"
				>
					<div v-for="(arg, i) in computedQueryTemplate.args" :key="i">
						<div class="xs-12 fs3 c6 fw7 pb2">{{ $t(arg.localeKey) }}</div>
						<component
							:is="arg.component"
							v-model="queryTemplateArgs[arg.id]"
							size="small"
						/>
					</div>
				</div>
				<j-button class="mt6" size="small" @click="buildQuery">{{
					$t("confirm")
				}}</j-button>
			</div>
		</core-modal>
		<!-- Input liquid condition modal -->
		<core-modal
			:name="`inputLiquidConditionModal-${modalKey}`"
			:headline="$t('success_condition')"
		>
			<div class="xs-12 p4">
				<div class="xs-12 fs3 c6 fw7 pb2">{{ $t("success_condition") }}</div>
				<!-- Select template -->
				<j-base-select
					size="small"
					:options="liquidConditions"
					class="xs-12"
					v-model="selectedLiquidTemplateId"
				/>
				<!-- liquid args -->
				<div
					v-if="
						computedLiquidConditionTemplate.args &&
							computedLiquidConditionTemplate.args.length
					"
					class="bt1 bc7 mt6 pt2"
				>
					<div
						v-for="(arg, i) in computedLiquidConditionTemplate.args"
						:key="i"
					>
						<div class="xs-12 fs3 c6 fw7 pb2">{{ $t(arg.localeKey) }}</div>
						<component
							:is="arg.component"
							v-model="liquidTemplateArgs[arg.id]"
							size="small"
						/>
					</div>
				</div>

				<j-button class="mt6" size="small" @click="buildLiquidCondition">{{
					$t("confirm")
				}}</j-button>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JBaseSelect from "@/components/JBaseSelect"
import JBaseInputNumber from "@/components/JBaseInputNumber"
import JButton from "@/components/JButton"
import JBaseRadios from "@/components/JBaseRadios"
import CrmIcon from "@/components/CrmIcon"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreMonacoEditor from "@/modules/core/components/CoreMonacoEditor"
import CoreSelectCourse from "@/modules/core/components/CoreSelectCourse"

import queriesTemplates from "../utils/queriesTemplates.js"
import liquidConditionsTemplates from "../utils/liquidConditionsTemplates"

export default {
	name: "AchievementLogic",
	components: {
		JBaseSelect,
		JBaseInputNumber,
		JButton,
		JBaseRadios,
		CrmIcon,
		CoreModal,
		CoreMonacoEditor,
		CoreSelectCourse
	},

	props: {
		isLoading: Boolean,

		showRemoveBtn: {
			type: Boolean,
			required: false,
			default: false
		},

		/*
		 * AchievementLogic
		 */
		value: Object
	},

	computed: {
		computedQuery() {
			return this.value?.graphQlQuery
		},
		computedLiquidCondition() {
			return this.value?.liquidCondition
		},

		computedQueryTemplate() {
			return (
				queriesTemplates.find(t => t.id === this.selectedQueryTemplateId) || {}
			)
		},

		computedLiquidConditionTemplate() {
			return (
				liquidConditionsTemplates.find(
					t => t.id === this.selectedLiquidTemplateId
				) || {}
			)
		},

		andAchievementLogicList() {
			return this.value?.and || []
		},

		orAchievementLogicList() {
			return this.value?.or || []
		}
	},

	data() {
		return {
			// Options for the selects; 'value' is the ids of the templates in queriesTemplates and liquidConditionsTemplates
			graphQlQueries: [
				{
					name: this.$t("all_courses"),
					value: "all_courses"
				},
				{
					name: this.$t("one_course"),
					value: "one_course"
				}
			],
			liquidConditions: [
				{
					name: this.$t("course_is_passed"),
					value: "course_is_passed"
				},
				{
					name: this.$t("number_of_courses_passed"),
					value: "number_of_courses_passed"
				}
			],

			achievementLogicTypes: [
				{ name: this.$t("simple"), value: "simple" },
				{ name: this.$t("and"), value: "and" },
				{ name: this.$t("or"), value: "or" }
			],

			selectedAchievementLogicType: this.value?.and?.length
				? "and"
				: this.value?.or?.length
				? "or"
				: "simple",

			selectedQueryTemplateId: null,
			queryTemplateArgs: {},
			selectedLiquidTemplateId: null,
			liquidTemplateArgs: {},

			// Needed because of the recursivity, to get the inputs for the correct AchievementLogic
			modalKey: this.generateModalKey()
		}
	},

	methods: {
		onChangeAchievementType(type) {
			// Empty the logic on change type
			const emptyLogic = {
				and: null,
				or: null,
				graphQlQuery: null,
				liquidCondition: null
			}
			if (type === "simple") this.$emit("input", emptyLogic)
			if (type === "and")
				this.$emit("input", { ...emptyLogic, and: [{ ...emptyLogic }] })
			if (type === "or")
				this.$emit("input", { ...emptyLogic, or: [{ ...emptyLogic }] })
			this.selectedAchievementLogicType = type
		},

		buildQuery() {
			this.$modal.hide(`inputQueryModal-${this.modalKey}`)

			this.$emit("input", {
				...this.value,
				graphQlQuery: this.computedQueryTemplate?.build({
					...this.queryTemplateArgs
				})
			})
		},

		buildLiquidCondition() {
			this.$modal.hide(`inputLiquidConditionModal-${this.modalKey}`)

			this.$emit("input", {
				...this.value,
				liquidCondition: this.computedLiquidConditionTemplate?.build({
					...this.liquidTemplateArgs
				})
			})
		},

		addLogicToAnd() {
			this.$emit("input", {
				and: [
					...this.andAchievementLogicList,
					{
						liquidCondition: null,
						graphQlQuery: null,
						and: null,
						or: null
					}
				]
			})
		},

		updateLogicOfAnd(index, newVal) {
			const newLogic = this.andAchievementLogicList.map((item, i) => {
				return i !== index ? item : { ...item, ...newVal }
			})
			this.$emit("input", {
				and: newLogic
			})
		},

		removeLogicFromAnd(index) {
			this.$emit("input", {
				and: this.andAchievementLogicList.filter((logic, i) => i !== index)
			})
		},

		addLogicToOr() {
			this.$emit("input", {
				or: [
					...this.orAchievementLogicList,
					{
						liquidCondition: null,
						graphQlQuery: null,
						and: null,
						or: null
					}
				]
			})
		},

		updateLogicOfOr(index, newVal) {
			const newLogic = this.orAchievementLogicList.map((item, i) => {
				return i !== index ? item : { ...item, ...newVal }
			})
			this.$emit("input", {
				or: newLogic
			})
		},

		removeLogicFromOr(index) {
			this.$emit("input", {
				or: this.orAchievementLogicList.filter((logic, i) => i !== index)
			})
		},

		generateModalKey: () => Date.now() + "-" + Math.random(10000)
	}
}
</script>
